<script setup lang="ts">
/**
 * A single labelled form control with VeeValidate plumbing.
 *
 * Wires up label-for, aria-invalid, aria-describedby for error/help text.
 * Renders the actual control via the default scoped slot — passes the
 * input handlers and current value down.
 *
 * **Validation timing (consistent across every editor):**
 * - On change → validate immediately (`handleChange(v, true)`).
 * - But the error is only *displayed* once the field has been blurred
 *   (`meta.touched`). So the user can type their first guess without
 *   being heckled red mid-keystroke, gets feedback the moment they tab
 *   away, and from then on every keystroke updates (or clears) the
 *   error live until they save.
 *
 * Every editor that needs validation should reach for this + AppForm.
 * Don't roll a manual `safeParse` flow in a new dialog — it diverges
 * from the rest of the app and the audit will rightly complain.
 *
 * Usage:
 *   <AppFormField name="email" label="Email" v-slot="{ value, handlers, invalid }">
 *     <AppInput :model-value="value" :invalid="invalid" v-bind="handlers" />
 *   </AppFormField>
 */
import { useField } from 'vee-validate'
import { computed, useId } from 'vue'

import AppLabel from './AppLabel.vue'

interface Props {
  name: string
  label?: string
  description?: string
  required?: boolean
  /**
   * When false, hides the label visually but keeps it in DOM for screen
   * readers. Use sparingly — most fields should have visible labels.
   */
  labelVisible?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  label: undefined,
  description: undefined,
  required: false,
  labelVisible: true,
})

// `useField` registers with the enclosing VeeValidate <Form>.
const { value, errorMessage, handleBlur, handleChange, meta } = useField<unknown>(() => props.name)

const fieldId = useId()
const errorId = `${fieldId}-error`
const descriptionId = `${fieldId}-description`

const invalid = computed(() => !!errorMessage.value && meta.touched)

const describedBy = computed(() => {
  const ids: string[] = []
  if (props.description) ids.push(descriptionId)
  if (invalid.value) ids.push(errorId)
  return ids.length ? ids.join(' ') : undefined
})

const handlers = computed(() => ({
  'onUpdate:modelValue': (v: unknown) => handleChange(v, true),
  onBlur: handleBlur,
  id: fieldId,
  'aria-invalid': invalid.value || undefined,
  'aria-describedby': describedBy.value,
}))
</script>

<template>
  <div class="flex flex-col gap-1.5">
    <AppLabel
      v-if="label"
      :for="fieldId"
      :required="required"
      :class="!labelVisible ? 'sr-only' : ''"
    >
      {{ label }}
    </AppLabel>
    <slot :value="value" :handlers="handlers" :invalid="invalid" :field-id="fieldId" />
    <!-- Description and error coexist — the description often carries
         the format hint ("max 2 decimals", "blank lines become
         paragraphs") that the user actually needs to read when the
         error fires, not before. Hiding it on error was the wrong
         trade-off. The error is announced via role="alert" so screen
         readers still get it as the primary message. -->
    <p v-if="description" :id="descriptionId" class="text-xs text-muted-foreground">
      {{ description }}
    </p>
    <p v-if="invalid" :id="errorId" role="alert" class="text-xs text-destructive">
      {{ errorMessage }}
    </p>
  </div>
</template>
